<template>
  <g ref="el" class="input-curvehandle">
    <template v-if="point.bezierOut != null && editable === true">
      <line
        class="input-curvehandle__bezier-line"
        :x1="unmapXValue(point.x)"
        :y1="point.y / yFactor"
        :x2="unmapXValue(point.x + point.bezierOut.x)"
        :y2="(point.y + point.bezierOut.y) / yFactor"
      />
      <circle
        ref="bezierOutPointRef"
        class="input-curvehandle__bezier-circle"
        :cx="unmapXValue(point.x + point.bezierOut.x)"
        :cy="(point.y + point.bezierOut.y) / yFactor"
        :r="small ? 1 : 2"
        @click.alt.right.prevent="reduceBezier('out')"
        @contextmenu="onBezierContextMenu($event, 'out')"
      />
    </template>

    <template v-if="point.bezierIn != null && editable === true">
      <line
        class="input-curvehandle__bezier-line"
        :x1="unmapXValue(point.x)"
        :y1="point.y / yFactor"
        :x2="unmapXValue(point.x + point.bezierIn.x)"
        :y2="(point.y + point.bezierIn.y) / yFactor"
      />
      <circle
        ref="bezierInPointRef"
        class="input-curvehandle__bezier-circle"
        :cx="unmapXValue(point.x + point.bezierIn.x)"
        :cy="(point.y + point.bezierIn.y) / yFactor"
        :r="small ? 1 : 2"
        @click.alt.right.prevent="reduceBezier('in')"
        @contextmenu="onBezierContextMenu($event, 'in')"
      />
    </template>

    <circle
      ref="pointRef"
      class="input-curvehandle__point"
      :cx="unmapXValue(point.x)"
      :cy="point.y / yFactor"
      :r="small ? 2 : 4"
      @click.alt.right.prevent="spreadBeziers()"
      @click.ctrl.prevent="$emit('delete')"
      @click.middle.prevent="$emit('delete')"
      @pointerenter="state.isHovering = true"
      @pointerleave="state.isHovering = false"
      @contextmenu.prevent="onPointContextMenu"
    />

    <transition name="input-curvehandle__handlenums">
      <g
        v-if="state.isDragging || state.isHovering"
        :transform="`translate(${labelX}, ${point.y / yFactor + 8})`"
        style="pointer-events: none; font-size: 6px"
      >
        <rect
          :x="-labelWidth / 2"
          y="-3"
          :width="labelWidth"
          height="10"
          rx="3"
          class="input-curvehandle__handletextbg"
        />
        <text
          transform="scale(1,-1)"
          text-anchor="middle"
          text-rendering="optimizeLegibility"
          class="input-curvehandle__handletext"
        >
          <template v-if="moveDimensions.includes('x')"
            >{{ labelXText }}&puncsp;|&puncsp;</template
          >{{ labelYText }}
        </text>
      </g>
    </transition>

    <foreignObject v-if="coordsPopover.visible">
      <popup-dialog
        :visible="coordsPopover.visible"
        :x="coordsPopover.x"
        :y="coordsPopover.y"
        :backdrop="false"
        :width="300"
        @close="coordsPopover.visible = false"
      >
        <template #header>
          <div class="pt-3"></div>
        </template>
        <template #default="{ close }">
          <form
            class="line gap-3 p-4 pt-0"
            @submit.prevent="close()"
            @keydown.esc="close()"
          >
            <input-number
              label="x"
              :value="point.x"
              :min="minX"
              :max="maxX"
              name="x"
              step="any"
              @update:value="updatePoint({ x: $event })"
              @enter="close()"
            />
            <input-number
              label="y"
              :min="-Infinity"
              :value="point.y"
              name="y"
              step="any"
              @update:value="updatePoint({ y: $event })"
              @enter="close()"
            />
            <button
              class="button-icon p-0 self-end pb-1"
              type="button"
              @click="close($event)"
            >
              <lucide-icon name="x" />
            </button>
          </form>
        </template>
      </popup-dialog>
    </foreignObject>
  </g>
</template>

<script setup lang="ts">
import {
  reactive,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
  ref
} from "vue";
import { useI18n } from "vue-i18n";
import interact from "interactjs";
import SvgHelper from "@/helpers/utility/SvgHelper.js";
import { vectorLength, mapNumber } from "@/helpers/utility/MathHelper.js";

import InputNumber from "@core/input/InputNumber.vue";
import PopupDialog from "@core/ui/PopupDialog.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import type { Interactable } from "@interactjs/types";
import type { ContextmenuAction } from "@core/ui/rightClickMenu.store";

interface Props {
  point: {
    id?: number;
    x: number;
    y: number;
    bezierIn?: { x: number; y: number };
    bezierOut?: { x: number; y: number };
  };
  minY?: number;
  maxY?: number;
  minX: number | null;
  maxX: number | null;
  startXValue: number; // the value at the left edge of the graph
  endXValue: number; // the value at the right edge of the graph
  editable?: boolean;
  bezierMinX: number | null;
  bezierMaxX: number | null;
  minBezierDistance?: number;
  deletable?: boolean;
  yFactor?: number;
  gridColumnWidth?: number;
  moveDimensions?: "x" | "y" | "xy";
  small?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  minY: 0,
  maxY: 100,
  editable: true,
  minBezierDistance: 5,
  deletable: true,
  yFactor: 1,
  gridColumnWidth: 10,
  moveDimensions: "xy",
  small: false
});

const emit = defineEmits<{
  (e: "delete"): void;
  (e: "move-point", point: Props["point"]): void;
  (e: "dragend", point?: Props["point"]): void;
  (
    e: "contextmenu",
    event: {
      x: number;
      y: number;
      actions: ContextmenuAction[];
    }
  ): void;
}>();

const { n, t, locale } = useI18n();

const state = reactive({
  isHovering: false,
  isDragging: false
});

let pointInteract: Interactable | null = null;
let bezierInInteract: Interactable | null = null;
let bezierOutInteract: Interactable | null = null;

const coordsPopover = reactive({
  visible: false,
  x: 0,
  y: 0
});

const el = ref<SVGElement>();
const pointRef = ref<HTMLElement>();
const bezierOutPointRef = ref<HTMLElement>();
const bezierInPointRef = ref<HTMLElement>();

const labelWidth = computed(() => {
  const x = labelXText.value;
  const y = labelYText.value;
  const length = y.length + x.length + 3;
  return length * 3;
});

const labelX = computed(() => {
  const x = unmapXValue(props.point.x);
  const right = x + labelWidth.value / 2;
  const left = x - labelWidth.value / 2;
  if (right > 116) {
    return 116 - labelWidth.value / 2;
  }
  if (left < -22) {
    return -22 + labelWidth.value / 2;
  }
  return x;
});

const labelXText = computed(() => {
  return formatNumberLabel(props.point.x);
});

const labelYText = computed(() => {
  return formatNumberLabel(props.point.y);
});

watch(
  () => props.point.bezierIn,
  () => {
    nextTick(() => {
      addInteractions();
    });
  }
);
watch(
  () => props.point.bezierOut,
  () => {
    nextTick(() => {
      addInteractions();
    });
  }
);

onMounted(() => {
  addInteractions();
});
onBeforeUnmount(() => {
  if (pointInteract != null) {
    pointInteract.unset();
  }
  if (bezierInInteract) bezierInInteract.unset();
  if (bezierOutInteract) bezierOutInteract.unset();
});

function addInteractions() {
  const pointElement = pointRef.value;
  const bezierInElement = bezierInPointRef.value;
  const bezierOutElement = bezierOutPointRef.value;
  
  if (pointElement) {
    if (props.editable && !pointInteract) {
      pointInteract = interact(pointElement).draggable({
        lockAxis: props.moveDimensions,
        cursorChecker: (action, interactable, element, interacting) => {
          console.log("interacting", interacting);
          if (interacting) {
            return "";
          }
          return "move";
        },
        onstart: () => {
          state.isDragging = true;
        },
        onmove: (event) => {
          onDrag(event);
        },
        onend: () => {
          state.isDragging = false;
          onDragEnd();
        }
      });
    }
  }

  if (bezierOutElement) {
    // add drag handlers for the bezier out point:
    if (props.point.bezierOut != null && props.editable && !bezierOutInteract) {
      bezierOutInteract = interact(bezierOutElement).draggable({
        onmove: (event) => {
          onBezierDrag(event, props.point.bezierOut, "out");
        },
        onend: () => {
          onDragEnd();
        }
      });
    }
  }

  if (bezierInElement) {
    // add drag handlers for the bezier in point:
    if (props.point.bezierIn != null && props.editable && !bezierInInteract) {
      bezierInInteract = interact(bezierInElement).draggable({
        onmove: (event) => {
          onBezierDrag(event, props.point.bezierIn, "in");
        },
        onend: () => {
          onDragEnd();
        }
      });
    }
  }
}

function onDrag(event: MouseEvent) {
  if (!props.editable) return;
  if (!el.value) return;
  event.preventDefault();
  event.stopPropagation();

  // convert global drag coordinates to local svg coordinates:
  const { x: dragX, y: dragY } = SvgHelper.toLocal(el.value, {
    x: event.clientX,
    y: event.clientY
  });
  let { x, y } = props.point;
  if (props.moveDimensions.includes("x")) {
    x = dragX;
  }
  if (props.moveDimensions.includes("y")) {
    y = dragY;
  }
  if (event.shiftKey) {
    x = Math.round(x / props.gridColumnWidth) * props.gridColumnWidth;
    y = Math.round(y / 10) * 10;
  }

  // scale values
  x = mapXValue(Math.round(x));
  y = Math.round(y) * props.yFactor;

  const minY = props.minY * props.yFactor;
  const maxY = props.maxY * props.yFactor;

  // enforce bounds
  if (y < minY) y = minY;
  else if (y > maxY) y = maxY;

  if (props.minX != null && x < props.minX) x = props.minX;
  else if (props.maxX != null && x > props.maxX) x = props.maxX;

  // save new position
  const newPoint = { ...props.point };
  newPoint.x = x;
  newPoint.y = y;
  emit("move-point", newPoint);
}

function onBezierDrag(
  event: MouseEvent,
  bezierPoint: { x: number; y: number } | undefined,
  type: "in" | "out"
) {
  if (!props.editable) return;
  if (!el.value) return;
  event.preventDefault();
  event.stopPropagation();

  // transform the global drag coordinates to local svg coordinates:
  let { x, y } = SvgHelper.toLocal(el.value, {
    x: event.clientX,
    y: event.clientY
  });
  const newBezier = bezierPoint ? { ...bezierPoint } : { x: 0, y: 0 };
  if (event.shiftKey) {
    x = Math.round(x / props.gridColumnWidth) * props.gridColumnWidth;
    y = Math.round(y / 10) * 10;
  }

  // scale values
  y = Math.round(y) * props.yFactor;
  x = mapXValue(Math.round(x));

  // enforce X bounds
  if (type === "in") {
    if (props.bezierMinX != null && x < props.bezierMinX) x = props.bezierMinX;
    else if (x >= props.point.x) x = props.point.x - 0.001;
  } else if (type === "out") {
    if (x <= props.point.x) x = props.point.x + 0.001;
    else if (props.bezierMaxX != null && x > props.bezierMaxX) x = props.bezierMaxX;
  }

  newBezier.y = y - props.point.y;
  newBezier.x = x - props.point.x;

  // Check if the bezier is too close to the main point.
  // We're using the local coordinates here, because the bezier
  // is scaled differently in y and x directions.
  const localMainPoint = {
    x: unmapXValue(props.point.x),
    y: props.point.y / props.yFactor
  };
  const localBezierCoord = {
    x: unmapXValue(props.point.x + newBezier.x),
    y: (props.point.y + newBezier.y) / props.yFactor
  };
  const distance = vectorLength({
    x: localBezierCoord.x - localMainPoint.x,
    y: localBezierCoord.y - localMainPoint.y
  });

  if (distance < props.minBezierDistance) {
    newBezier.x = 0;
    newBezier.y = 0;
  }

  const newPoint = { ...props.point };
  if (type === "in") {
    newPoint.bezierIn = newBezier;
  }
  if (type === "out") {
    newPoint.bezierOut = newBezier;
  }
  emit("move-point", newPoint);
}

function onDragEnd() {
  if (!props.editable) return;
  emit("dragend", props.point);
}

function updatePoint(newData: { x?: number; y?: number }) {
  emit("move-point", {
    ...props.point,
    ...newData
  });
  emit("dragend");
}

function spreadBeziers() {
  const point = props.point;
  const newPoint = { ...point };
  if (point.bezierIn) {
    newPoint.bezierIn = scaleBezier(point.bezierIn, "in");
  }
  if (point.bezierOut) {
    newPoint.bezierOut = scaleBezier(point.bezierOut, "out");
  }
  emit("move-point", newPoint);
  emit("dragend");
}

function scaleBezier(
  bezier: { x: number; y: number },
  type = "in",
  targetLength = 20
) {
  const length = vectorLength(bezier);
  const scaledTargetLength = Math.abs(mapXValue(targetLength) - mapXValue(0));
  if (length == 0) {
    return {
      ...bezier,
      x: type == "in" ? -scaledTargetLength : scaledTargetLength,
      y: 0
    };
  } else {
    const xNormalized = bezier.x / length;
    const yNormalized = bezier.y / length;
    return {
      ...bezier,
      x: xNormalized * scaledTargetLength,
      y: yNormalized * scaledTargetLength
    };
  }
}

function reduceBezier(type = "both") {
  const newPoint = { ...props.point };
  if (type === "in" || type === "both") {
    newPoint.bezierIn = { x: 0, y: 0 };
  }
  if (type === "out" || type === "both") {
    newPoint.bezierOut = { x: 0, y: 0 };
  }
  emit("move-point", newPoint);
  emit("dragend");
}

function onPointContextMenu(event: MouseEvent) {
  const contextMenuActions: ContextmenuAction[] = [];
  if (
    (props.point.bezierIn && props.point.bezierIn.x != 0) ||
    (props.point.bezierOut && props.point.bezierOut.x != 0)
  ) {
    contextMenuActions.push({
      title: t("actions.removeBeziers"),
      callback: () => {
        reduceBezier("both");
      }
    });
  }
  if (
    (props.point.bezierIn && props.point.bezierIn.x == 0) ||
    (props.point.bezierOut && props.point.bezierOut.x == 0)
  ) {
    contextMenuActions.push({
      title: t("actions.addBeziers"),
      callback: () => {
        spreadBeziers();
      }
    });
  }
  if (props.deletable) {
    contextMenuActions.push({
      title: t("actions.deletePoint"),
      callback: () => {
        emit("delete");
      }
    });
  }
  contextMenuActions.push({
    title: t("actions.inputPointValues"),
    callback: () => {
      coordsPopover.visible = true;
      coordsPopover.x = event.clientX;
      coordsPopover.y = event.clientY;
    }
  });
  emit("contextmenu", {
    x: event.clientX,
    y: event.clientY,
    actions: contextMenuActions
  });
}

function onBezierContextMenu(event: MouseEvent, type: string) {
  if (event.defaultPrevented) return;
  event.preventDefault();

  emit("contextmenu", {
    x: event.clientX,
    y: event.clientY,
    actions: [
      {
        title: t("actions.removeBeziers"),
        callback: () => {
          reduceBezier(type);
        }
      }
    ]
  });
}

function mapXValue(value: number) {
  const num = mapNumber(value, 0, 100, props.startXValue, props.endXValue);
  return Math.round(num * 10) / 10;
}

function unmapXValue(value: number) {
  return mapNumber(value, props.startXValue, props.endXValue, 0, 100);
}

function formatNumberLabel(num: number) {
  if (Math.abs(num) < 100) return n(num);

  // if (this.xStepSize >= 1000) {
  //   const nearest = Math.round(num / 1000);
  //   return nearest + "K";
  // }

  return new Intl.NumberFormat(locale.value, {
    notation: "standard",
    compactDisplay: "short"
  }).format(num);
}
</script>

<style lang="scss">
.input-curvehandle {
  &__bezier-line {
    stroke: var(--color-action);
    stroke-width: 1px;
  }

  &__bezier-circle {
    fill: var(--color-action);
    touch-action: none;
  }

  &__point {
    fill: var(--color-action);
    touch-action: none;
  }

  &__handletextbg {
    fill: var(--color-action);
    touch-action: none;
  }
  &__handletext {
    fill: var(--color-action-contrast);
  }

  &__handlenums {
    &-enter-active,
    &-leave-active {
      transition: opacity 0.5s;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
    }
  }
}
</style>
